import { useEffect, useState } from "react";

import { Button, Col, List, Modal, Row } from "@shared/ui";

import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { Flex, Input, Space } from "antd";
import styles from "./styles.module.scss";

import {
  useCreatePermissionGroup,
  useDeletePermissionGroup,
  useEditPermissionGroup,
  useGetPermissionGroups,
} from "@entities/permissionGroups/lib/hooks";
import { PermissionGroupForm } from "@entities/permissionGroups/ui/PermissionGroupForm";
import { ToastService } from "@shared/lib/services";
import {
  ICreatePermissionGroup,
  IEditPermissionGroup,
  PermissionGroup,
} from "@shared/lib/types/permissions";
import { TextWithTooltip } from "@shared/ui/TextWithTooltip";
import { Plus, Search, Shield } from "lucide-react";

export function PermissionGroups() {
  const [searchQuery, setSearchQuery] = useState<string>("");

  const handleSearchChange = (val: string) => {
    setSearchQuery(val);
  };

  const { permissionGroups, isFetching } = useGetPermissionGroups(searchQuery);

  const { createPermissionGroup, isCreating, isCreated } =
    useCreatePermissionGroup();

  const { editPermissionGroup, isEditing, isEdited } = useEditPermissionGroup();

  const {
    deletePermissionGroup,
    isDeleting,
    isDeleted,

    variables: deletePermissionGroupVariables,
  } = useDeletePermissionGroup();
  const [selectedPermissionGroup, setSelectedPermissionGroup] =
    useState<PermissionGroup | null>(null);

  const [isModalVisible, setModalVisible] = useState(false);
  const [highlightedGroupId, setHighlightedGroupId] = useState<string>("");

  const handleCreatePermissionGroup = () => {
    setSelectedPermissionGroup(null);
    setModalVisible(true);
  };

  const handleSubmit = (
    permissionGroup: ICreatePermissionGroup | IEditPermissionGroup
  ) => {
    if (selectedPermissionGroup) {
      editPermissionGroup(permissionGroup as IEditPermissionGroup);
    } else {
      createPermissionGroup(permissionGroup as ICreatePermissionGroup);
    }
  };

  const handleDeletePermissionGroup = (permissionGroupId: string) => {
    deletePermissionGroup(permissionGroupId);
  };

  useEffect(() => {
    if (isCreated || isEdited) {
      setModalVisible(false);
      ToastService.success("Permission Groups updated successfully");
    }
  }, [isCreated, isEdited]);

  useEffect(() => {
    if (isDeleted) {
      ToastService.success("Permission Group deleted successfully");
    }
  }, [isDeleted]);

  return (
    <div>
      <div>
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <Button
            className={styles.createButton}
            type="dashed"
            onClick={() => handleCreatePermissionGroup()}
            icon={<Plus size={14} />}
          >
            Create Permission Group
          </Button>
          <Input
            placeholder="Search By Group Name"
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            allowClear
            style={{ width: "100%" }}
            prefix={<Search size={14}/>}
          />
        </Space>

        <List
          loading={isFetching}
          dataSource={permissionGroups || []}
          split={false}
          style={{ marginTop: "16px" }}
          pagination={{
            pageSize: 10,
            hideOnSinglePage: true,
            style: { marginTop: "16px", textAlign: "right" }
          }}
          renderItem={(group) => (
            <List.Item style={{ padding: 0, border: "none", marginBottom: "16px" }}>
              <div
                key={group._id}
                className={styles.policyCard}
                style={{ width: "100%" }}
                onMouseEnter={() => setHighlightedGroupId(group._id)}
                onMouseLeave={() => setHighlightedGroupId("")}
              >
                <Row gutter={10} align="middle">
                  <Col flex="none">
                    <div className={styles.policyIcon}>
                      <Shield size={20} />
                    </div>
                  </Col>
                  <Col flex="auto" className={styles.policyInfo} style={{ minWidth: 0 }}>
                    {/* <Typography.Title
                      level={4}
                      style={{ margin: 0, fontSize: "15px" }}
                      ellipsis={{ tooltip: true }}
                    >
                      {group.name}
                    </Typography.Title> */}
                    <Flex vertical>
                    <TextWithTooltip 
                      text={group.name}
                      limit={40}
                      textClassName={styles.cardTitleStyle}
                    />
                    <TextWithTooltip 
                      text={group.description || "-"}
                      limit={40}
                    />
                    </Flex>
                    <div className={styles.policyMeta}>
                      <span>
                        {`${group.permissions?.length || 0} Permissions`}
                      </span>
                      <div className={styles.dot} />
                      <span>
                        Created by: {group.createdBy?.firstName || "-"}
                      </span>
                    </div>
                  </Col>
                  <Col flex="none" style={{ minWidth: "80px", textAlign: "right" }}>
                    {highlightedGroupId === group._id && (
                      <Flex gap={4} justify="end">
                        <Button
                          type="text"
                          icon={<EditOutlined />}
                          onClick={() => {
                            setSelectedPermissionGroup(group);
                            setModalVisible(true);
                          }}
                        />
                        <Button
                          type="text"
                          icon={<DeleteOutlined />}
                          onClick={() => handleDeletePermissionGroup(group._id)}
                          loading={
                            isDeleting && deletePermissionGroupVariables === group._id
                          }
                          danger
                        />
                      </Flex>
                    )}
                  </Col>
                </Row>
              </div>
            </List.Item>
          )}
          locale={{
            emptyText: (
              <div style={{ textAlign: "center", padding: "40px 0", color: "#9ba3b2" }}>
                No permission groups found
              </div>
            )
          }}
        />
      </div>
      <Modal
        title={
          selectedPermissionGroup
            ? "Edit Permission Group"
            : "Create Permission Group"
        }
        open={isModalVisible}
        onCancel={() => {
          setModalVisible(false);
          setSelectedPermissionGroup(null);
        }}
        footer={null}
        destroyOnClose
      >
        <PermissionGroupForm
          onSubmit={(values) => handleSubmit(values)}
          isSubmitting={isCreating || isEditing}
          onCancel={() => {
            setModalVisible(false);
            setSelectedPermissionGroup(null);
          }}
          initialValues={selectedPermissionGroup}
        />
      </Modal>
    </div>
  );
}
